import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  DegreeType,
  Visibility,
  useGetCurrentUserQuery,
  useGetDegreesQuery,
  useSwitchStudentDegreeMutation,
} from '@teachur/http-client';
import type { Degree, UserDegree } from '@teachur/http-client';
import { getErrorMessage } from '@new/lib';
import { Role } from '@teachur/http-client';

interface UseStudentDegreeSwitchArgs {
  studentId: number;
  enrolledDegrees: UserDegree[];
  onSwitched?: () => void;
}

export const crossesSubscriptionBoundary = (
  fromType: string | undefined,
  toType: string | undefined
): boolean => {
  const fromMaster = fromType === DegreeType.Master;
  const toMaster = toType === DegreeType.Master;
  return fromMaster !== toMaster;
};

export const useStudentDegreeSwitch = ({
  studentId,
  enrolledDegrees,
  onSwitched,
}: UseStudentDegreeSwitchArgs) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: allDegrees = [], isLoading: isLoadingDegrees } = useGetDegreesQuery();

  const isAdmin = currentUser?.role === Role.Admin || currentUser?.role === Role.SuperAdmin;

  const currentDegree = enrolledDegrees[0]?.degree;
  const currentDegreeId = currentDegree?.id;

  const [pendingDegree, setPendingDegree] = useState<Degree | null>(null);

  const { mutateAsync, isPending } = useSwitchStudentDegreeMutation({
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to switch the degree.'));
    },
  });

  const selectableDegrees = useMemo(
    () => allDegrees.filter((d) => d.id !== currentDegreeId && d.visibility === Visibility.Public),
    [allDegrees, currentDegreeId]
  );

  const requestSwitch = (degree: Degree) => {
    setPendingDegree(degree);
  };

  const cancelSwitch = () => {
    if (!isPending) {
      setPendingDegree(null);
    }
  };

  const confirmSwitch = async () => {
    if (!pendingDegree) {
      return;
    }
    try {
      await mutateAsync({ userId: studentId, toDegreeId: pendingDegree.id });
      toast.success(`Student switched to ${pendingDegree.name}.`);
      setPendingDegree(null);
      onSwitched?.();
    } catch {
      // Error toast handled in the mutation's onError; keep the modal open.
    }
  };

  return {
    isAdmin,
    isLoadingDegrees,
    currentDegree,
    selectableDegrees,
    pendingDegree,
    isPending,
    pendingChangesSubscription: crossesSubscriptionBoundary(
      currentDegree?.type,
      pendingDegree?.type
    ),
    requestSwitch,
    cancelSwitch,
    confirmSwitch,
  };
};
